import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { TranscriptStatus } from '@/entities/meeting'
import { useSession } from '@/entities/session'
import {
  SegmentRow,
  TRANSCRIPT_EXPORTERS,
  type TranscriptExportFormat,
  transcriptQueryOptions,
} from '@/entities/transcript'
import { cn, downloadFilename, downloadTextFile, pluralRu } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { Spinner } from '@/shared/ui/spinner'
import { ZoneNote } from '@/shared/ui/zone-note'

const EXPORT_FORMATS: TranscriptExportFormat[] = ['txt', 'srt', 'vtt']

interface TranscriptSectionProps {
  meetingId: string
  status: TranscriptStatus
  progress: number
  title: string
  dateIso: string
}

// Расшифровка прошедшей встречи — общая поверхность сотрудника и заказчика:
// им обоим она открыта ровно вместе со встречей.
export function TranscriptSection({
  meetingId,
  status,
  progress,
  title,
  dateIso,
}: TranscriptSectionProps) {
  const { data, isLoading } = useQuery({
    ...transcriptQueryOptions(meetingId),
    enabled: status === 'ready',
  })
  const { session } = useSession()
  const [expanded, setExpanded] = useState(false)

  // Прогресс обновляется поллингом detail (refetchInterval) — бар живой.
  if (status === 'queued') {
    return (
      <div className="flex max-w-md flex-col gap-2">
        <p className="text-text-tertiary">Идёт расшифровка… {progress}&nbsp;%</p>
        <div
          role="progressbar"
          aria-label="Прогресс подготовки транскрипта"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-1 overflow-hidden rounded-pill bg-bg-raised"
        >
          <div
            className="h-full rounded-pill bg-accent transition-[width] duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    )
  }
  if (status === 'failed') return <p className="text-accent">Не удалось подготовить транскрипт.</p>
  if (isLoading || !data) return <Spinner label="Загрузка транскрипта" />

  // Свои реплики — справа (как в мессенджере): матчим по username сессии,
  // фолбэк — по отображаемому имени (у гостей и заказчиков username'а нет).
  function isOwn(speakerUsername: string | undefined, speakerName: string): boolean {
    if (session?.username && speakerUsername) return speakerUsername === session.username
    return session?.name != null && speakerName === session.name
  }

  function exportFile(format: TranscriptExportFormat) {
    if (!data) return
    const { render, mime } = TRANSCRIPT_EXPORTERS[format]
    downloadTextFile(downloadFilename(title, dateIso, 'транскрипт', format), render(data), mime)
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Транскрипт свёрнут по умолчанию: это одна из функций страницы,
          а не её цель — полотно реплик не должно съедать всю высоту. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Button variant="secondary" size="sm" onClick={() => setExpanded((v) => !v)}>
          <Icon
            name="chevron-down"
            size={16}
            className={cn('transition-transform', expanded && 'rotate-180')}
          />
          {expanded
            ? 'Свернуть'
            : `Показать · ${data.segments.length}\u00A0${pluralRu(data.segments.length, 'реплика', 'реплики', 'реплик')}`}
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <span className="label">Скачать:</span>
          {EXPORT_FORMATS.map((format) => (
            <Button key={format} variant="secondary" size="sm" onClick={() => exportFile(format)}>
              .{format}
            </Button>
          ))}
        </div>
        {/* Реплики — колонка моментов: зона названа один раз над ней. Без
            якоря в транскрипте абсолютного времени нет, подписывать нечего. */}
        {data.anchor && <ZoneNote label="время реплик" className="ml-auto" />}
      </div>
      {expanded &&
        data.segments.map((segment) => (
          <SegmentRow
            key={`${segment.startMs}-${segment.speaker}`}
            segment={segment}
            own={isOwn(segment.speakerUsername, segment.speakerName)}
            {...(data.anchor ? { anchor: data.anchor } : {})}
          />
        ))}
    </div>
  )
}
